import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Pencil, Trash2, Upload, X, Image } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to handle image URLs
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    event: '',
    date: new Date().toISOString().split('T')[0],
    image: null
  });
  const [bulkFormData, setBulkFormData] = useState({
    event: '',
    date: new Date().toISOString().split('T')[0],
    images: []
  });
  const [previewImages, setPreviewImages] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      const response = await apiService.admin.getPhotos();
      setPhotos(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('event', formData.event);
      formDataToSend.append('date', formData.date);
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingPhoto) {
        await apiService.admin.updatePhoto(editingPhoto.id, formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      } else {
        await apiService.admin.createPhoto(formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      }
      
      loadPhotos();
      closeModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan foto', variant: 'destructive' });
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (bulkFormData.images.length === 0) {
      toast({ title: 'Error', description: 'Pilih minimal 1 foto', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('event', bulkFormData.event);
      formDataToSend.append('date', bulkFormData.date);
      
      // Append all images
      bulkFormData.images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      const response = await apiService.admin.createPhotosBulk(formDataToSend);
      toast({ 
        title: 'Berhasil', 
        description: response.data.message || `${bulkFormData.images.length} foto berhasil diupload`
      });
      
      loadPhotos();
      closeBulkModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal mengupload foto', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus foto ini?')) return;
    try {
      await apiService.admin.deletePhoto(id);
      toast({ title: 'Berhasil', description: 'Foto berhasil dihapus' });
      loadPhotos();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus foto', variant: 'destructive' });
    }
  };

  const openModal = (photo = null) => {
    if (photo) {
      setEditingPhoto(photo);
      setFormData({
        title: photo.title,
        event: photo.event,
        date: new Date(photo.date).toISOString().split('T')[0],
        image: null
      });
    } else {
      setEditingPhoto(null);
      setFormData({
        title: '',
        event: '',
        date: new Date().toISOString().split('T')[0],
        image: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPhoto(null);
  };

  const openBulkModal = () => {
    setBulkFormData({
      event: '',
      date: new Date().toISOString().split('T')[0],
      images: []
    });
    setPreviewImages([]);
    setShowBulkModal(true);
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setPreviewImages([]);
    setBulkFormData({
      event: '',
      date: new Date().toISOString().split('T')[0],
      images: []
    });
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files);
    setBulkFormData({ ...bulkFormData, images: files });
    
    // Generate previews
    const previews = files.map(file => ({
      name: file.name,
      url: URL.createObjectURL(file)
    }));
    setPreviewImages(previews);
  };

  const removePreviewImage = (index) => {
    const newImages = [...bulkFormData.images];
    newImages.splice(index, 1);
    setBulkFormData({ ...bulkFormData, images: newImages });
    
    // Revoke old URL to prevent memory leaks
    URL.revokeObjectURL(previewImages[index].url);
    const newPreviews = [...previewImages];
    newPreviews.splice(index, 1);
    setPreviewImages(newPreviews);
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Kelola Foto</h2>
          <div className="flex space-x-2">
            <button 
              onClick={openBulkModal} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              data-testid="bulk-upload-btn"
            >
              <Upload className="h-5 w-5" />
              <span>Unggah Banyak</span>
            </button>
            <button 
              onClick={() => openModal()} 
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              data-testid="add-photo-btn"
            >
              <Plus className="h-5 w-5" />
              <span>Tambah Foto</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <img src={getImageUrl(photo.image)} alt={photo.title} className="w-full h-40 object-cover" />
                <div className="p-3">
                  <h3 className="font-semibold text-sm line-clamp-2 mb-1">{photo.title}</h3>
                  <p className="text-xs text-gray-500 mb-2">{photo.event}</p>
                  <div className="flex space-x-2">
                    <button onClick={() => openModal(photo)} className="flex-1 text-blue-600 text-xs"><Pencil className="h-3 w-3 mx-auto" /></button>
                    <button onClick={() => handleDelete(photo.id)} className="flex-1 text-red-600 text-xs"><Trash2 className="h-3 w-3 mx-auto" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {photos.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Image className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Belum ada foto. Klik tombol di atas untuk menambahkan.</p>
          </div>
        )}

        {/* Single Photo Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">{editingPhoto ? 'Edit Foto' : 'Tambah Foto'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Judul</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Event</label>
                  <input type="text" value={formData.event} onChange={(e) => setFormData({ ...formData, event: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tanggal</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Foto {editingPhoto && '(kosongkan jika tidak ingin mengubah)'}</label>
                  <input type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} className="w-full px-4 py-2 border rounded-md" required={!editingPhoto} />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="flex-1 bg-gray-200 px-4 py-2 rounded-md">Batal</button>
                  <button type="submit" className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md">{editingPhoto ? 'Update' : 'Simpan'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Unggah Banyak Foto</h3>
                <button onClick={closeBulkModal} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleBulkSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Event/Acara</label>
                    <input 
                      type="text" 
                      value={bulkFormData.event} 
                      onChange={(e) => setBulkFormData({ ...bulkFormData, event: e.target.value })} 
                      className="w-full px-4 py-2 border rounded-md" 
                      placeholder="Nama event atau acara"
                      required 
                      data-testid="bulk-event-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Tanggal</label>
                    <input 
                      type="date" 
                      value={bulkFormData.date} 
                      onChange={(e) => setBulkFormData({ ...bulkFormData, date: e.target.value })} 
                      className="w-full px-4 py-2 border rounded-md" 
                      required 
                      data-testid="bulk-date-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Pilih Foto (bisa pilih banyak)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors">
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple
                      onChange={handleBulkFileChange}
                      className="hidden"
                      id="bulk-file-input"
                      data-testid="bulk-file-input"
                    />
                    <label htmlFor="bulk-file-input" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Klik untuk pilih foto atau drag & drop</p>
                      <p className="text-sm text-gray-400 mt-1">Bisa pilih banyak foto sekaligus</p>
                    </label>
                  </div>
                </div>

                {/* Preview Images */}
                {previewImages.length > 0 && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium">Preview ({previewImages.length} foto dipilih)</label>
                      <button 
                        type="button"
                        onClick={() => {
                          previewImages.forEach(p => URL.revokeObjectURL(p.url));
                          setPreviewImages([]);
                          setBulkFormData({ ...bulkFormData, images: [] });
                        }}
                        className="text-red-600 text-sm hover:underline"
                      >
                        Hapus Semua
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                      {previewImages.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={preview.url} 
                            alt={preview.name}
                            className="w-full h-20 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removePreviewImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <p className="text-xs text-gray-500 truncate mt-1">{preview.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button 
                    type="button" 
                    onClick={closeBulkModal} 
                    className="flex-1 bg-gray-200 px-4 py-2 rounded-md"
                    disabled={uploading}
                  >
                    Batal
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 disabled:opacity-50"
                    disabled={uploading || previewImages.length === 0}
                    data-testid="bulk-upload-submit"
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        <span>Mengupload...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>Unggah {previewImages.length} Foto</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPhotos;
